import React, { useEffect, useRef, useState } from 'react';
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
} from 'framer-motion';
import './ExperienceLayer.scss';

const ExperienceLayer = () => {
  const reduceMotion = useReducedMotion();
  const [showIntro, setShowIntro] = useState(!reduceMotion);
  const [progress, setProgress] = useState(0);
  const [cursorActive, setCursorActive] = useState(false);
  const cursorActiveRef = useRef(false);
  const mouseX = useMotionValue(-100);
  const mouseY = useMotionValue(-100);
  const cursorX = useSpring(mouseX, { stiffness: 850, damping: 48, mass: 0.22 });
  const cursorY = useSpring(mouseY, { stiffness: 850, damping: 48, mass: 0.22 });

  useEffect(() => {
    if (reduceMotion) {
      setShowIntro(false);
      return undefined;
    }

    const startedAt = performance.now();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const tick = window.setInterval(() => {
      const elapsed = performance.now() - startedAt;
      setProgress(Math.min(100, Math.round((elapsed / 900) * 100)));
    }, 30);
    const finish = window.setTimeout(() => {
      setProgress(100);
      setShowIntro(false);
      document.body.style.overflow = previousOverflow;
    }, 980);

    return () => {
      window.clearInterval(tick);
      window.clearTimeout(finish);
      document.body.style.overflow = previousOverflow;
    };
  }, [reduceMotion]);

  useEffect(() => {
    if (reduceMotion || !window.matchMedia('(pointer: fine)').matches) return undefined;

    document.documentElement.classList.add('has-creative-cursor');

    const handlePointerMove = (event) => {
      mouseX.set(event.clientX);
      mouseY.set(event.clientY);

      const target = event.target instanceof Element ? event.target : null;
      const isInteractive = Boolean(target?.closest('a, button, input, textarea, [data-cursor]'));
      if (isInteractive !== cursorActiveRef.current) {
        cursorActiveRef.current = isInteractive;
        setCursorActive(isInteractive);
      }
    };

    const handlePointerLeave = () => {
      mouseX.set(-100);
      mouseY.set(-100);
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    document.documentElement.addEventListener('mouseleave', handlePointerLeave);

    return () => {
      document.documentElement.classList.remove('has-creative-cursor');
      window.removeEventListener('pointermove', handlePointerMove);
      document.documentElement.removeEventListener('mouseleave', handlePointerLeave);
    };
  }, [mouseX, mouseY, reduceMotion]);

  return (
    <>
      <AnimatePresence>
        {showIntro && (
          <motion.div
            className="experience-loader"
            initial={{ opacity: 1 }}
            exit={{ clipPath: 'inset(0 0 100% 0)' }}
            transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
            aria-hidden="true"
          >
            <div className="experience-loader__top">
              <span>Portfolio / 2026</span>
              <span>Dhaka · GMT+6</span>
            </div>
            <div className="experience-loader__center">
              <p>knowaminul</p>
              <strong>{String(progress).padStart(2, '0')}<small>%</small></strong>
            </div>
            <div className="experience-loader__bottom">
              <span>Loading experience</span>
              <div><i style={{ transform: `scaleX(${progress / 100})` }} /></div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!reduceMotion && (
        <motion.div
          className={`creative-cursor ${cursorActive ? 'is-active' : ''}`}
          style={{ x: cursorX, y: cursorY }}
          aria-hidden="true"
        >
          <span />
        </motion.div>
      )}
    </>
  );
};

export default ExperienceLayer;
